/**
 * useTheme — управление темой (light/dark/system).
 *
 * Тема живёт в localStorage('kb-theme'). При system — следим за
 * prefers-color-scheme и применяем соответствующую.
 *
 * Тема применяется через data-theme="dark" атрибут на documentElement.
 * CSS переменные в global.css переопределяются через [data-theme="dark"].
 */
import { useCallback, useEffect, useState } from "react";


export type ThemeMode = "light" | "dark" | "system";
const STORAGE_KEY = "kb-theme";


function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}


function applyTheme(mode: ThemeMode): void {
  const effective = mode === "system" ? getSystemTheme() : mode;
  if (effective === "dark") document.documentElement.setAttribute("data-theme", "dark");
  else document.documentElement.removeAttribute("data-theme");
}


/** Грузит сохранённую тему из localStorage и применяет ДО монтирования React.
 * Вызвать в main.tsx чтобы избежать flash of wrong theme.
 * Default — "light" (НЕ "system"): тема per-device, юзер сам включает явно;
 * не хотим чтобы тёмная сама включалась по prefers-color-scheme. */
export function bootstrapTheme(): void {
  try {
    const saved = (localStorage.getItem(STORAGE_KEY) as ThemeMode) || "light";
    applyTheme(saved);
  } catch {
    applyTheme("light");
  }
}


export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try { return (localStorage.getItem(STORAGE_KEY) as ThemeMode) || "light"; }
    catch { return "light"; }
  });

  const setTheme = useCallback((next: ThemeMode) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setMode(next);
    applyTheme(next);
  }, []);

  // Подписка на смену системной темы (если выбран system)
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  // Эффективная тема (с учётом system)
  const effective: "light" | "dark" =
    mode === "system" ? getSystemTheme() : mode;

  return { mode, effective, setTheme };
}
